import React, { forwardRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Link as LinkIcon, CheckCircle2, Mail, MapPin, Phone, ShieldCheck, User } from "lucide-react";
import { FIRMA, LOGO_URL, DISCLAIMER } from "@/lib/branding";
import { docLabel, INVOICE_TYPES } from "@/lib/constants";
import { computeAmounts, documentPositions, manualBillingLines, fmtCurrency, fmtDate, fmtDateTime, positionLineNet, positionQuantity, effectiveBank, isSwissQrEligible, buildSwissQrPayload, isGiroCodeEligible, buildGiroCodePayload, taxHintForCountry } from "@/lib/quote-helpers";

const QuotePreview = forwardRef(({ quote, total, company = {}, steuer = {}, attachments = [], signature = {} }, ref) => {
  const taxInfo = taxHintForCountry(quote.kunde_land, company, quote.currency);
  const currency = quote.currency || "EUR";
  const isInvoice = INVOICE_TYPES.includes(quote.dokument_typ);
  const eb = effectiveBank(steuer, quote);
  const docTitle = docLabel(quote.dokument_typ, company).toUpperCase();
  const docNrLabel = isInvoice ? "Rechnungsnummer" : `${docLabel(quote.dokument_typ, company)}nummer`;
  const validLabel = isInvoice ? "Fällig bis" : "Gültig bis";
  const layout = quote.layout || "classic";
  const headerBg =
    layout === "modern"
      ? "var(--brand-primary)"
      : layout === "minimal"
      ? "#ffffff"
      : "var(--brand-bg)";
  const headerTitleColor = layout === "modern" ? "#ffffff" : "var(--brand-ink)";
  const selectedAttachments = (attachments || []).filter((a) =>
    (quote.attachments || []).includes(a.id)
  );

  // Manuelle Zeilen und gespeicherte Positionen sind getrennte Eingabewege,
  // werden im fertigen Dokument aber als eine fortlaufende Tabelle abgerechnet.
  const savedPositions = Array.isArray(quote.positions) ? quote.positions : [];
  const hasManualBilling = manualBillingLines(quote).length > 0;
  const hasPositions = savedPositions.length > 0 || hasManualBilling;
  const positionsList = hasPositions ? documentPositions(quote) : [];
  const textKey = (value) => String(value || "").replace(/\s+/g, " ").trim().toLocaleLowerCase("de-DE");
  const generalTitleKey = textKey(quote.leistung_titel);
  const generalDescriptionKey = textKey(quote.leistung_beschreibung);
  const titleAlreadyInPosition = !!generalTitleKey && savedPositions.some((position) => textKey(position.titel) === generalTitleKey);
  const descriptionAlreadyInPosition = !!generalDescriptionKey && savedPositions.some((position) =>
    (position.bullets || []).some((detail) => textKey(detail) === generalDescriptionKey),
  );
  // Aus Lead-/Vorlagenabläufen kann der gleiche Text bereits in einer Position
  // gespeichert sein. Dann zeigen wir ihn nur dort und nie ein zweites Mal.
  const showGeneralTitle = !!generalTitleKey && !titleAlreadyInPosition;
  const showGeneralDescription = !!generalDescriptionKey && !descriptionAlreadyInPosition;
  const showGeneralServiceBlock = hasPositions && !hasManualBilling && (showGeneralTitle || showGeneralDescription);
  // Bewertungs-Disclaimer nur bei echtem Bewertungs-Angebot zeigen
  const isBewertungsAngebot =
    !hasPositions &&
    ((quote.leistung_titel || "") + " " + (quote.leistung_beschreibung || ""))
      .toLowerCase()
      .includes("bewertung");
  // Default Bewertungs-Einleitung erkennen, um sie im Multi-Paket-Modus zu unterdrücken
  const isLegacyBewertungsIntro =
    !quote.einleitung ||
    (quote.einleitung || "").includes("Bewertungsmeldung") ||
    (quote.einleitung || "").includes("Bewertungsmeldungen");
  const effectiveIntro = savedPositions.length > 0 && !hasManualBilling
    ? isLegacyBewertungsIntro
      ? `vielen Dank für Ihre Anfrage. Nachfolgend erhalten Sie ${docLabel(quote.dokument_typ, company)} mit den passenden Leistungspaketen für Ihre Online-Sichtbarkeit. Bitte wählen Sie die für Sie relevanten Pakete aus – die ausgewählten Positionen werden in den Gesamtbetrag eingerechnet.`
      : quote.einleitung
    : (quote.einleitung && quote.einleitung.trim()) ||
      `vielen Dank für Ihre Anfrage. Nachfolgend erhalten Sie ${docLabel(quote.dokument_typ, company)}.`;

  return (
    <div
      ref={ref}
      id="quote-preview"
      data-testid="quote-preview"
      className="bg-white mx-auto shadow-xl"
      style={{
        width: "794px",
        minHeight: "1123px",
        color: "var(--brand-ink)",
        fontFamily: "Inter, sans-serif",
      }}
    >
      {/* Top */}
      <div
        data-pdf-section="header"
        data-testid={`preview-layout-${layout}`}
        className="px-12 pt-10 pb-6"
        style={{
          background: headerBg,
          borderBottom: layout === "minimal" ? "2px solid var(--brand-primary)" : "none",
        }}
      >
        <div className="flex items-start justify-between gap-6">
          {LOGO_URL ? (
            <img
              src={LOGO_URL}
              alt="logo"
              crossOrigin="anonymous"
              style={{ width: 140, height: 140, objectFit: "contain" }}
            />
          ) : (
            <div
              style={{
                width: 140, height: 140, borderRadius: 16,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "var(--brand-primary)", color: "#fff",
                fontFamily: "'Playfair Display', serif", fontSize: 56, fontWeight: 700,
              }}
            >
              {(FIRMA.firma || FIRMA.name || "·").trim().charAt(0).toUpperCase()}
            </div>
          )}
          <div className="text-right flex-1">
            <h1
              className="font-bold leading-tight"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 38,
                color: headerTitleColor,
                letterSpacing: "0.02em",
              }}
            >
              {docTitle}
            </h1>
            <div className="mt-4 text-sm space-y-1">
              <div className="flex justify-end gap-2">
                <span className="text-[var(--brand-muted)]">{docNrLabel}:</span>
                <span className="font-medium min-w-[180px] text-left border-b border-[var(--brand-primary)]">
                  {quote.angebot_nr || "—"}
                </span>
              </div>
              {quote.ref_doc_nr && (
                <div className="flex justify-end gap-2" data-testid="ref-doc-line">
                  <span className="text-[var(--brand-muted)]">Bezug:</span>
                  <span className="font-medium min-w-[180px] text-left border-b border-[var(--brand-primary)]">
                    {quote.ref_doc_nr}
                  </span>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <span className="text-[var(--brand-muted)]">Datum:</span>
                <span className="font-medium min-w-[180px] text-left border-b border-[var(--brand-primary)]">
                  {fmtDate(quote.angebot_datum)}
                </span>
              </div>
              <div className="flex justify-end gap-2">
                <span className="text-[var(--brand-muted)]">{validLabel}:</span>
                <span className="font-medium min-w-[180px] text-left border-b border-[var(--brand-primary)]">
                  {fmtDate(quote.gueltig_bis)}
                </span>
              </div>
              {quote.leistungsdatum && (
                <div className="flex justify-end gap-2">
                  <span className="text-[var(--brand-muted)]">Leistungsdatum:</span>
                  <span className="font-medium min-w-[180px] text-left border-b border-[var(--brand-primary)]">
                    {quote.leistungsdatum}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Contact band */}
      <div
        data-pdf-section="contact"
        className="px-12 py-4 grid grid-cols-4 gap-4 text-xs"
        style={{ background: "var(--brand-border-light)" }}
      >
        <div className="flex items-start gap-2">
          <User className="w-4 h-4 text-[var(--brand-primary)]" />
          <div>
            <div className="font-semibold">{FIRMA.name}</div>
            <div className="text-[var(--brand-muted)]">{FIRMA.firma}</div>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 text-[var(--brand-primary)]" />
          <div>
            <div>{FIRMA.strasse}</div>
            <div>{FIRMA.plz_ort}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-[var(--brand-primary)]" />
          <span>{FIRMA.telefon}</span>
        </div>
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-[var(--brand-primary)]" />
          <span className="break-all">{FIRMA.email}</span>
        </div>
      </div>

      {/* Body */}
      <div className="px-12 py-8">
        <div data-pdf-section="parteien">
        <h2
          className="text-xl font-semibold mb-3"
          style={{
            fontFamily: "'Playfair Display', serif",
            color: "var(--brand-primary)",
            borderBottom: "1px solid var(--brand-border)",
            paddingBottom: 4,
          }}
        >
          {isInvoice ? "Rechnungsparteien" : "Dokumentparteien"}
        </h2>

        <div className="grid grid-cols-2 gap-6 mb-8">
          <div className="rounded p-4" style={{ background: "var(--brand-bg)" }}>
            <div
              className="text-[11px] uppercase tracking-wider mb-2"
              style={{ color: "var(--brand-primary)", fontWeight: 600 }}
            >
              {isInvoice ? "Rechnungssteller" : "Auftragnehmerin"}
            </div>
            <div className="text-sm leading-relaxed">
              <div className="font-semibold">{FIRMA.name}</div>
              <div>{FIRMA.firma}</div>
              <div>{FIRMA.strasse}</div>
              <div>{FIRMA.plz_ort}</div>
              <div>{FIRMA.land}</div>
              <div className="mt-2">
                <span className="text-[var(--brand-muted)]">Tel.: </span>
                {FIRMA.telefon}
              </div>
              <div>
                <span className="text-[var(--brand-muted)]">E-Mail: </span>
                {FIRMA.email}
              </div>
              {steuer.steuernummer && (
                <div>
                  <span className="text-[var(--brand-muted)]">Steuernummer: </span>
                  {steuer.steuernummer}
                </div>
              )}
              {steuer.ustid && (
                <div>
                  <span className="text-[var(--brand-muted)]">USt-IdNr.: </span>
                  {steuer.ustid}
                </div>
              )}
            </div>
          </div>

          <div className="rounded p-4" style={{ background: "var(--brand-bg)" }}>
            <div
              className="text-[11px] uppercase tracking-wider mb-2"
              style={{ color: "var(--brand-primary)", fontWeight: 600 }}
            >
              {isInvoice ? "Rechnungsempfänger" : `Empfänger für ${docLabel(quote.dokument_typ, company)}`}
            </div>
            <div className="text-sm leading-relaxed">
              <div className="font-semibold">
                {quote.anrede} {quote.kunde_name || "—"}
              </div>
              <div>{quote.kunde_firma || ""}</div>
              <div>{quote.kunde_strasse || ""}</div>
              <div>{quote.kunde_plz_ort || ""}</div>
              <div>{quote.kunde_land || ""}</div>
              {quote.kunde_telefon && (
                <div className="mt-2">
                  <span className="text-[var(--brand-muted)]">Tel.: </span>
                  {quote.kunde_telefon}
                </div>
              )}
              {quote.kunde_email && (
                <div>
                  <span className="text-[var(--brand-muted)]">E-Mail: </span>
                  {quote.kunde_email}
                </div>
              )}
              {quote.kunde_ustid && (
                <div>
                  <span className="text-[var(--brand-muted)]">USt-IdNr.: </span>
                  {quote.kunde_ustid}
                </div>
              )}
            </div>
          </div>
        </div>
        </div>{/* /parteien */}

        <div data-pdf-section="intro">
        <p className="text-sm mb-4">
          Sehr geehrte{quote.anrede === "Herr" ? "r" : ""} {quote.anrede}{" "}
          {quote.kunde_name || "Musterkunde"},
        </p>
        <p
          className="text-sm mb-6 leading-relaxed whitespace-pre-line"
          data-testid="preview-einleitung"
        >
          {isInvoice
            ? "vielen Dank für Ihren Auftrag. Nachfolgend erhalten Sie unsere Rechnung über die erbrachte Leistung. Bitte überweisen Sie den Rechnungsbetrag innerhalb des unten genannten Zahlungsziels auf das angegebene Konto."
            : effectiveIntro}
        </p>
        </div>{/* /intro */}

        <div data-pdf-section="leistung">
        <h2
          className="text-xl font-semibold mb-3"
          style={{
            fontFamily: "'Playfair Display', serif",
            color: "var(--brand-primary)",
            borderBottom: "1px solid var(--brand-border)",
            paddingBottom: 4,
          }}
        >
          Leistungsumfang
        </h2>

        {showGeneralServiceBlock && (
          <div className="mb-5 rounded border p-4" style={{ background: "var(--brand-bg)", borderColor: "var(--brand-border)" }} data-testid="general-service-description">
            {showGeneralTitle && <div className="font-semibold text-[var(--brand-ink)]">{quote.leistung_titel}</div>}
            {showGeneralDescription && <div className={`${showGeneralTitle ? "mt-1 " : ""}text-sm leading-relaxed whitespace-pre-line text-[var(--brand-ink)]`}>{quote.leistung_beschreibung}</div>}
          </div>
        )}

        {hasPositions ? (
          <div className="mb-6 overflow-hidden rounded border" style={{ borderColor: "var(--brand-border)" }} data-testid="positions-list">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr style={{ background: "var(--brand-border-light)" }}>
                  <th className="w-12 px-3 py-2 text-left font-semibold">Nr.</th>
                  <th className="px-3 py-2 text-left font-semibold">Leistung / Beschreibung</th>
                  <th className="w-24 px-3 py-2 text-right font-semibold">Menge</th>
                  <th className="w-28 px-3 py-2 text-right font-semibold">Einzelpreis</th>
                  <th className="w-28 px-3 py-2 text-right font-semibold">Gesamt</th>
                </tr>
              </thead>
              <tbody>
                {positionsList.map((pos, idx) => {
                  const isIn = !!pos.include_in_total;
                  const details = (pos.bullets || []).filter((b) => b && b.trim());
                  return (
                    <tr key={pos.id || idx} className="border-t" style={{ borderColor: "var(--brand-border)" }}>
                      <td className="px-3 py-3 align-top font-medium">{isIn ? "✓ " : ""}{idx + 1}</td>
                      <td className="px-3 py-3 align-top"><div className="font-semibold">{pos.titel || "Position"}</div>{details.length > 0 && <ul className="mt-1 space-y-0.5 text-xs leading-relaxed text-[var(--brand-ink)]">{details.map((detail, detailIndex) => <li key={detailIndex}>• {detail}</li>)}</ul>}{!isIn && <div className="mt-1 text-[10px] italic text-[var(--brand-muted)]">Optionale Position – nicht im Gesamtbetrag eingerechnet</div>}</td>
                      <td className="px-3 py-3 text-right align-top">{positionQuantity(pos)} {pos.einheit || ""}</td>
                      <td className="px-3 py-3 text-right align-top">{fmtCurrency(pos.preis, currency)}</td>
                      <td className="px-3 py-3 text-right align-top font-semibold">{fmtCurrency(positionLineNet(pos), currency)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!positionsList.some((p) => p.include_in_total) && (
              <p
                className="border-t px-3 py-2 text-xs text-[var(--brand-muted)] italic"
                style={{ borderColor: "var(--brand-border)" }}
                data-testid="no-total-hint"
              >
                Bitte gewünschte Pakete auswählen – nur ausgewählte Positionen
                fließen in den Gesamtbetrag ein.
              </p>
            )}
          </div>
        ) : (
          <table className="w-full text-sm mb-6 border-collapse">
            <thead>
              <tr style={{ background: "var(--brand-border-light)" }}>
                <th className="text-left py-2 px-3 font-semibold w-12">Nr.</th>
                <th className="text-left py-2 px-3 font-semibold">Leistung</th>
                <th className="text-right py-2 px-3 font-semibold w-20">Menge</th>
                <th className="text-right py-2 px-3 font-semibold w-32">
                  Einzelpreis
                </th>
                <th className="text-right py-2 px-3 font-semibold w-32">Gesamt</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-[var(--brand-border)]">
                <td className="py-3 px-3 align-top">1</td>
                <td className="py-3 px-3 align-top">
                  <div className="font-semibold">{quote.leistung_titel}</div>
                  <div className="text-xs text-[#5b5b5b] mt-1 leading-relaxed">
                    {quote.leistung_beschreibung}
                  </div>
                </td>
                <td className="py-3 px-3 text-right align-top">
                  {quote.anzahl_bewertungen}
                </td>
                <td className="py-3 px-3 text-right align-top">
                  {fmtCurrency(quote.preis_pro_bewertung)}
                </td>
                <td className="py-3 px-3 text-right align-top font-semibold">
                  {fmtCurrency(total)}
                </td>
              </tr>
            </tbody>
          </table>
        )}

        {(() => {
          const anySelected = hasPositions && positionsList.some((p) => p.include_in_total);
          // Gesamtbetrag nur zeigen, wenn:
          // - Single-Item-Modus (legacy)
          // - ODER Mehrfach-Positionen UND mindestens eine ausgewählt
          const showTotal = !hasPositions || anySelected;
          if (!showTotal) return null;
          const a = computeAmounts(quote);
          return (
            <div className="flex justify-end mb-6">
              <div className="w-80 rounded overflow-hidden" style={{ background: "var(--brand-bg)" }}>
                <div className="flex justify-between px-4 py-2">
                  <span className="text-sm text-[var(--brand-muted)]">Nettobetrag</span>
                  <span className="text-sm">{fmtCurrency(a.net, a.currency)}</span>
                </div>
                {a.document_discount > 0 && <div className="flex justify-between px-4 py-1 text-sm" style={{ color: "var(--brand-primary)" }}><span>Dokumentrabatt</span><span>− {fmtCurrency(a.document_discount, a.currency)}</span></div>}
                {(a.tax_groups || []).filter((group) => group.tax > 0).map((group) => <div key={group.rate} className="flex justify-between px-4 py-1 text-sm text-[var(--brand-muted)]"><span>zzgl. MwSt {group.effective_rate}% auf {fmtCurrency(group.net, a.currency)}</span><span>{fmtCurrency(group.tax, a.currency)}</span></div>)}
                {a.tax === 0 && <div className="flex justify-between gap-3 px-4 py-1 text-xs text-[var(--brand-muted)]"><span>Hinweis</span><span className="text-right" data-testid="preview-tax-note">{a.reverse_charge ? "Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge)." : (a.kleinunternehmer || steuer?.tax_status === "kleinunternehmer") ? "Gemäß § 19 UStG wird keine Umsatzsteuer berechnet." : taxInfo.short}</span></div>}
                <div
                  className="flex justify-between px-4 py-3 mt-1"
                  style={{ background: "var(--brand-primary)", color: "white" }}
                >
                  <span className="font-semibold">Gesamtbetrag</span>
                  <span className="font-bold" style={{ fontFamily: "'Playfair Display', serif", fontSize: 18 }}>
                    {fmtCurrency(a.gross, a.currency)}
                  </span>
                </div>
                {a.skonto_amount > 0 && <div className="flex justify-between px-4 py-2 text-sm text-[var(--brand-muted)]"><span>bei {quote.skonto_percent}% Skonto{quote.skonto_days ? ` innerhalb ${quote.skonto_days} Tagen` : ""}</span><span>{fmtCurrency(a.skonto_gross, a.currency)}</span></div>}
                {a.deposit > 0 && (
                  <>
                    <div className="flex justify-between px-4 py-2 text-sm" style={{ color: "var(--brand-primary)" }}>
                      <span className="font-medium">Anzahlung fällig</span>
                      <span className="font-semibold">{fmtCurrency(a.deposit, a.currency)}</span>
                    </div>
                    <div className="flex justify-between px-4 pb-3 text-sm text-[var(--brand-muted)]">
                      <span>Restbetrag</span>
                      <span>{fmtCurrency(a.rest, a.currency)}</span>
                    </div>
                  </>
                )}
                {a.prepaid > 0 && (
                  <>
                    <div className="flex justify-between px-4 py-2 text-sm text-[var(--brand-muted)]">
                      <span>abzgl. erhaltene Anzahlung</span>
                      <span>− {fmtCurrency(a.prepaid, a.currency)}</span>
                    </div>
                    <div className="flex justify-between px-4 pb-3 text-sm" style={{ color: "var(--brand-primary)" }}>
                      <span className="font-semibold">Noch zu zahlen</span>
                      <span className="font-semibold">{fmtCurrency(a.due, a.currency)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })()}
        </div>{/* /leistung */}

        {taxInfo.kind !== "national" && (
          <div
            data-pdf-section="tax-hint"
            className="rounded p-3 mb-4 text-xs"
            style={{ background: "var(--brand-bg)", borderLeft: "3px solid var(--brand-primary)" }}
            data-testid="preview-tax-hint"
          >
            <div
              className="font-semibold mb-1"
              style={{ color: "var(--brand-primary)" }}
            >
              {taxInfo.title}
            </div>
            <span>{taxInfo.long}</span>
          </div>
        )}

        {/* Disclaimer – nur anzeigen, wenn die Firma einen Hinweis hinterlegt hat */}
        {DISCLAIMER && DISCLAIMER.trim() && (
          <div
            data-pdf-section="disclaimer"
            className="rounded p-4 mb-6"
            style={{
              background: "var(--brand-bg)",
              borderLeft: "3px solid var(--brand-primary)",
            }}
            data-testid="disclaimer-block"
          >
            <div
              className="text-[11px] uppercase tracking-wider mb-1"
              style={{ color: "var(--brand-primary)", fontWeight: 600 }}
            >
              Wichtiger Hinweis
            </div>
            <p
              className="text-sm leading-relaxed"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontStyle: "italic",
                color: "var(--brand-ink)",
              }}
            >
              {DISCLAIMER}
            </p>
          </div>
        )}

        <div data-pdf-section="payment">
        <h2
          className="text-xl font-semibold mb-3"
          style={{
            fontFamily: "'Playfair Display', serif",
            color: "var(--brand-primary)",
            borderBottom: "1px solid var(--brand-border)",
            paddingBottom: 4,
          }}
        >
          Zahlungsbedingungen
        </h2>

        <div className="text-sm mb-6 space-y-2">
          <div>
            <span className="text-[var(--brand-muted)]">Zahlungsziel: </span>
            <span className="font-semibold">{quote.zahlungsziel}</span>
          </div>
          {quote.reseller_link && (
            <div className="flex items-start gap-2">
              <LinkIcon className="w-4 h-4 text-[var(--brand-primary)] mt-0.5" />
              <div>
                <div className="text-[var(--brand-muted)] text-xs">Zahlungslink</div>
                <a
                  href={quote.reseller_link}
                  className="break-all"
                  style={{ color: "var(--brand-primary)" }}
                >
                  {quote.reseller_link}
                </a>
              </div>
            </div>
          )}

          {isInvoice && (eb.iban || eb.bic || eb.bank) && (
            <div
              className="rounded p-3 mt-3"
              style={{ background: "var(--brand-bg)" }}
              data-testid="bank-block"
            >
              <div
                className="text-[11px] uppercase tracking-wider mb-2"
                style={{ color: "var(--brand-primary)", fontWeight: 600 }}
              >
                Bankverbindung
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {eb.kontoinhaber && (
                  <>
                    <span className="text-[var(--brand-muted)]">Kontoinhaber</span>
                    <span>{eb.kontoinhaber}</span>
                  </>
                )}
                {eb.iban && (
                  <>
                    <span className="text-[var(--brand-muted)]">IBAN</span>
                    <span className="font-mono">{eb.iban}</span>
                  </>
                )}
                {eb.bic && (
                  <>
                    <span className="text-[var(--brand-muted)]">BIC</span>
                    <span className="font-mono">{eb.bic}</span>
                  </>
                )}
                {eb.bank && (
                  <>
                    <span className="text-[var(--brand-muted)]">Bank</span>
                    <span>{eb.bank}</span>
                  </>
                )}
                {quote.angebot_nr && (
                  <>
                    <span className="text-[var(--brand-muted)]">Verwendungszweck</span>
                    <span>{quote.angebot_nr}</span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {isSwissQrEligible(steuer, quote) && (
          <div
            className="rounded p-4 mt-3 border-2 border-dashed"
            style={{ borderColor: "var(--brand-border)", background: "#ffffff" }}
            data-testid="swiss-qr-bill"
          >
            <div
              className="text-[11px] uppercase tracking-wider mb-3"
              style={{ color: "var(--brand-primary)", fontWeight: 600 }}
            >
              Schweizer Zahlungs-QR
            </div>
            <div className="flex items-center gap-4">
              <div className="relative shrink-0" style={{ width: 174, height: 174 }}>
                <QRCodeSVG
                  value={buildSwissQrPayload(steuer, quote)}
                  size={174}
                  level="M"
                  marginSize={4}
                  title="Schweizer Zahlungs-QR für die Banking-App"
                  data-testid="swiss-qr-code"
                />
                {/* Schweizer Kreuz in der Mitte */}
                <div
                  className="absolute top-1/2 left-1/2"
                  style={{
                    transform: "translate(-50%, -50%)",
                    width: 26,
                    height: 26,
                    background: "#000",
                    border: "2px solid #fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                    <div style={{ position: "relative", width: 16, height: 16 }}>
                      <div style={{ position: "absolute", top: 5, left: 1, width: 14, height: 5, background: "#fff" }} />
                      <div style={{ position: "absolute", top: 1, left: 5, width: 5, height: 14, background: "#fff" }} />
                  </div>
                </div>
              </div>
              <div className="text-xs space-y-1">
                <div className="text-[var(--brand-muted)]">
                  In der Banking-App scannen – Betrag, IBAN und Verwendungszweck werden übernommen.
                </div>
                <div>
                  <span className="text-[var(--brand-muted)]">Betrag: </span>
                  <span className="font-semibold">
                    {fmtCurrency(computeAmounts(quote).gross, quote.currency)}
                  </span>
                </div>
                {eb.iban && (
                  <div className="font-mono text-[11px]">{eb.iban}</div>
                )}
              </div>
            </div>
          </div>
        )}

        {isGiroCodeEligible(steuer, quote) && (
          <div
            className="rounded p-4 mt-3 border-2 border-dashed"
            style={{ borderColor: "var(--brand-border)", background: "#ffffff" }}
            data-testid="giro-code-bill"
          >
            <div
              className="text-[11px] uppercase tracking-wider mb-3"
              style={{ color: "var(--brand-primary)", fontWeight: 600 }}
            >
                Zahlungs-QR für Überweisung per Banking-App
            </div>
            <div className="flex items-center gap-4">
              <div className="shrink-0" style={{ width: 180, height: 180 }}>
                <QRCodeSVG
                  value={buildGiroCodePayload(steuer, quote)}
                  size={180}
                  level="M"
                  marginSize={4}
                  title="Zahlungs-QR für die Banking-App"
                  data-testid="giro-qr-code"
                />
              </div>
              <div className="text-xs space-y-1">
                <div className="text-[var(--brand-muted)]">
                  QR-Code in Ihrer Banking-App scannen – Betrag, IBAN &amp; Verwendungszweck werden automatisch übernommen.
                </div>
                <div>
                  <span className="text-[var(--brand-muted)]">Betrag: </span>
                  <span className="font-semibold">
                    {fmtCurrency(computeAmounts(quote).gross, quote.currency)}
                  </span>
                </div>
                {eb.iban && (
                  <div className="font-mono text-[11px]">{eb.iban}</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>{/* /payment */}

        {/* Notizen + Anlagen – nur anzeigen, wenn vorhanden */}
        {(quote.notizen || selectedAttachments.length > 0) && (
        <div data-pdf-section="hinweise">
        <h2
          className="text-xl font-semibold mb-3"
          style={{
            fontFamily: "'Playfair Display', serif",
            color: "var(--brand-primary)",
            borderBottom: "1px solid var(--brand-border)",
            paddingBottom: 4,
          }}
        >
          Hinweise & Anlagen
        </h2>
        {quote.notizen && (
          <p className="text-sm whitespace-pre-line mb-3 leading-relaxed">
            {quote.notizen}
          </p>
        )}
        {selectedAttachments.length > 0 && (
          <p className="text-sm text-[var(--brand-ink)] flex items-start gap-2 mb-6">
            <ShieldCheck className="w-4 h-4 text-[var(--brand-primary)] mt-0.5 flex-shrink-0" />
            <span>
              <span className="font-semibold">Anlagen:</span>{" "}
              {selectedAttachments.map((a, i) => (
                <span key={a.id}>
                  {a.label}
                  {i < selectedAttachments.length - 1 ? ", " : ""}
                </span>
              ))}
              . Die genannten Dokumente finden Sie im Anhang dieses PDFs.
            </span>
          </p>
        )}
        </div>
        )}{/* /hinweise */}

        <div data-pdf-section="signature" className="text-sm mt-6">
          Mit besten Grüßen
          {signature.exists && signature.data_url && (
            <div className="mt-2">
              <img
                src={signature.data_url}
                alt="Unterschrift"
                style={{ maxHeight: 70, maxWidth: 220, objectFit: "contain" }}
                data-testid="preview-admin-signature"
              />
            </div>
          )}
          {!signature.exists && <><br /><br /></>}
          <span
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22 }}
          >
            {FIRMA.name}
          </span>
          <br />
          <span className="text-[var(--brand-muted)] text-xs">{FIRMA.firma}</span>
        </div>

        {quote.customer_signed_at && quote.customer_signature && (
          <div
            data-pdf-section="customer-signed"
            className="mt-8 rounded p-4 border"
            style={{ borderColor: "var(--brand-primary)", background: "var(--brand-bg)" }}
            data-testid="preview-customer-signed"
          >
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-[var(--brand-primary)]" />
              <div className="font-semibold text-[var(--brand-ink)]">
                {docLabel(quote.dokument_typ, company)} angenommen
              </div>
            </div>
            <div className="text-xs text-[var(--brand-muted)] mb-2">
              {fmtDateTime(quote.customer_signed_at)}
              {quote.customer_signed_name && ` · ${quote.customer_signed_name}`}
            </div>
            <img
              src={quote.customer_signature}
              alt="Unterschrift Kunde"
              style={{ maxHeight: 70, maxWidth: 240, objectFit: "contain" }}
            />
          </div>
        )}
      </div>

      <div
        data-pdf-section="footer"
        className="px-12 py-3 text-[10px] flex justify-between border-t border-[var(--brand-border)]"
        style={{ background: "var(--brand-bg)", color: "var(--brand-muted)" }}
      >
        <span>{docLabel(quote.dokument_typ, company)}</span>
        <span>
          {FIRMA.firma} · {FIRMA.email}
        </span>
        <span>Seite 1 von 1</span>
      </div>
    </div>
  );
});

export { QuotePreview };
